import { AuthData, ResourceType, User } from '@admin/models';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, map, shareReplay } from 'rxjs';

/**
 * User management service for handling authentication, user data, and permissions
 * 
 * @description
 * The UserService is a centralized state management service that handles:
 * - User profile data and authentication information
 * - User permissions and privilege mappings
 * - Regional access control (country IDs)
 * - Session lifecycle management
 * 
 * This service uses RxJS BehaviorSubjects to provide reactive state management
 * with shareReplay for efficient subscription handling across components.
 * 
 * @service UserService
 * @providedIn 'root'
 * 
 * ## Key Features:
 * - **Reactive State Management**: Uses observables for real-time state updates
 * - **Session Management**: Handles complete user session lifecycle
 * - **Permission Control**: Manages fine-grained user permissions and privileges
 * - **Regional Access**: Supports country-based access control
 * - **Memory Efficient**: Uses shareReplay to optimize observable subscriptions
 * 
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private userService: UserService) {}
 * 
 * // Set user data
 * this.userService.setUser(userData);
 * 
 * // Subscribe to user changes
 * this.userService.user$.subscribe(user => {
 *   console.log('Current user:', user);
 * });
 * 
 * // Check permissions
 * const privileges = this.userService.userPrivilege;
 * if (privileges.tyres?.tc?.view) {
 *   // User can view tyre TC data
 * }
 * ```
 */
@Injectable({
  providedIn: 'root', // Add this
})
export class UserService {
  /**
   * BehaviorSubject holding the current user data
   * @private
   * @type {BehaviorSubject<User | null>}
   */
   private userSubject$ = new BehaviorSubject<User | null>(null);
   readonly isApprover$: Observable<boolean> = this.userSubject$.pipe(
     map(user => user?.isApprover === true),
     shareReplay(1),
  );
  
  /**
   * BehaviorSubject holding the current authentication data
   * @private
   * @type {BehaviorSubject<AuthData | null>}
   */
  private authSubject$ = new BehaviorSubject<AuthData | null>(null);
  
  /**
   * User privilege mappings by resource and business line
   * @private
   * @type {ResourceType}
   */
  private appUserPrivilege: ResourceType = {};
  
  /**
   * Current country ID for regional access control
   * @private
   * @type {string | null}
   */
  private countryIdValue: string | null = null;

  /**
   * Sets the current user data and emits the change to all subscribers
   * 
   * @description
   * Updates the user state with new user information. This method triggers
   * all subscribed components to receive the updated user data immediately.
   * Can be used to set user data from Auth0 profile, API responses, or to
   * clear user data by passing null.
   * 
   * @param {User | null} user - The user object containing profile and permission data, or null to clear
   * 
   * @example
   * ```typescript
   * // Set user data from API response
   * const userData = { firstName: 'John', email: 'john@example.com', role: 'admin' };
   * this.userService.setUser(userData);
   * 
   * // Clear user data
   * this.userService.setUser(null);
   * 
   * // Set user with access groups
   * const userWithPermissions = {
   *   firstName: 'Jane',
   *   email: 'jane@example.com',
   *   accessGroupList: [{ group: 'tyre-tc-view', view: true }],
   *   outsourcingGroups: ['CORE DATA']
   * };
   * this.userService.setUser(userWithPermissions);
   * ```
   */
  setUser(user: User | null) {
    this.userSubject$.next(user);
  }

  /**
   * Observable stream of user data changes
   * 
   * @description
   * Returns an observable that emits the current user data and all subsequent changes.
   * Uses shareReplay(1) to ensure new subscribers immediately receive the current value
   * and to prevent multiple subscriptions from creating separate data streams.
   * 
   * @returns {Observable<User | null>} Observable that emits user data or null when no user is set
   * 
   * @example
   * ```typescript
   * // Subscribe to user changes
   * this.userService.user$.subscribe(user => {
   *   if (user) {
   *     console.log(`Welcome ${user.firstName}!`);
   *     this.updateUserInterface(user);
   *   } else {
   *     console.log('No user logged in');
   *     this.redirectToLogin();
   *   }
   * });
   * 
   * // Use in templates with async pipe
   * // <div *ngIf="userService.user$ | async as user">{{user.firstName}}</div>
   * 
   * // Combine with other observables
   * combineLatest([this.userService.user$, this.configService.config$])
   *   .subscribe(([user, config]) => {
   *     // Handle user and config together
   *   });
   * ```
   */
  get user$(): Observable<User | null> {
    return this.userSubject$.asObservable().pipe(shareReplay(1));
  }

  onlyForApprovers<T>(source$: Observable<T>): Observable<T | null> {
    return combineLatest([source$, this.isApprover$]).pipe(
      map(([value, isApprover]) => isApprover ? value : null),
    );
  }

  /**
   * Sets the authentication data for the current session
   * 
   * @description
   * Updates the authentication state with data from Auth0 or other identity providers.
   * This includes authentication ID, email, and role information used for
   * security context and session management.
   * 
   * @param {AuthData | null} auth - Authentication data object or null to clear auth state
   * 
   * @example
   * ```typescript
   * // Set auth data from Auth0 callback
   * const authData = {
   *   authId: 'auth0|1234567890',
   *   email: 'user@example.com',
   *   role: 'admin'
   * };
   * this.userService.setAuth(authData);
   * 
   * // Clear auth data on logout
   * this.userService.setAuth(null);
   * 
   * // Set encoded auth data for security
   * const encodedAuth = {
   *   authId: user.sub,
   *   email: btoa(user.email), // Base64 encoded email
   *   role: user.role
   * };
   * this.userService.setAuth(encodedAuth);
   * ```
   */
  setAuth(auth: AuthData | null) {
    this.authSubject$.next(auth);
  }

  /**
   * Observable stream of authentication data changes
   * 
   * @description
   * Returns an observable that emits the current authentication state and all subsequent changes.
   * Used for tracking authentication status, user identity, and session validity.
   * Uses shareReplay(1) for immediate value delivery to new subscribers.
   * 
   * @returns {Observable<AuthData | null>} Observable that emits authentication data or null when not authenticated
   * 
   * @example
   * ```typescript
   * // Monitor authentication state
   * this.userService.auth$.subscribe(auth => {
   *   if (auth && auth.authId) {
   *     console.log('User authenticated:', auth.email);
   *     this.enableSecureFeatures();
   *   } else {
   *     console.log('User not authenticated');
   *     this.disableSecureFeatures();
   *   }
   * });
   * 
   * // Check authentication status
   * const isAuthenticated = await firstValueFrom(
   *   this.userService.auth$.pipe(map(auth => !!auth?.authId))
   * );
   * 
   * // Use for API request headers
   * this.userService.auth$.pipe(
   *   filter(auth => !!auth),
   *   switchMap(auth => this.apiService.getSecureData(auth.authId))
   * ).subscribe(data => console.log(data));
   * ```
   */
  get auth$(): Observable<AuthData | null> {
    return this.authSubject$.asObservable().pipe(shareReplay(1));
  }

  /**
   * Sets the user privilege mappings for fine-grained access control
   * 
   * @description
   * Updates the privilege mapping structure that defines what resources and business lines
   * the user can access. This data is typically processed from user access groups and
   * transformed into a hierarchical privilege structure for efficient permission checking.
   * 
   * @param {ResourceType} privilege - Hierarchical privilege structure mapping resources to business lines and permissions
   * 
   * @example
   * ```typescript
   * // Set privileges from processed access groups
   * const privileges = {
   *   tyres: {
   *     tc: { view: true, insert: false, update: true, delete: false },
   *     tk: { view: true, insert: true, update: false, delete: false }
   *   },
   *   vehicles: {
   *     '4w': { view: true, insert: true, update: true, delete: true },
   *     '2w': { view: true, insert: false, update: false, delete: false }
   *   }
   * };
   * this.userService.setUserPrivilege(privileges);
   * 
   * // Clear privileges
   * this.userService.setUserPrivilege({});
   * ```
   */
  setUserPrivilege(privilege: ResourceType) {
    this.appUserPrivilege = privilege;
  }

  /**
   * Gets the current user privilege mappings
   * 
   * @description
   * Returns the current privilege structure for permission checking throughout the application.
   * This data structure allows components to efficiently check if a user has specific
   * permissions for resources and business lines.
   * 
   * @returns {ResourceType} Current privilege mappings organized by resource and business line
   * 
   * @example
   * ```typescript
   * // Check specific permissions
   * const privileges = this.userService.userPrivilege;
   * 
   * // Check if user can view tyre TC data
   * if (privileges.tyres?.tc?.view) {
   *   this.showTyreData();
   * }
   * 
   * // Check if user can insert vehicle 4W data
   * if (privileges.vehicles?.['4w']?.insert) {
   *   this.enableCreateVehicle();
   * }
   * 
   * // Check if user has any privileges for a resource
   * const hasTyreAccess = Object.keys(privileges.tyres || {}).length > 0;
   * 
   * // Get all business lines for a resource
   * const tyreBusinessLines = Object.keys(privileges.tyres || {});
   * console.log('Available tyre business lines:', tyreBusinessLines);
   * ```
   */
  get userPrivilege(): ResourceType {
    return this.appUserPrivilege;
  }

  /**
   * Sets the country ID for regional access control and data filtering
   * 
   * @description
   * Updates the country identifier used for regional data access and filtering.
   * This value is typically derived from user's regional access groups (VH-MR-* pattern)
   * and used to filter data based on geographic permissions.
   * 
   * @param {string | null} countryId - Country identifier for regional filtering or null to clear
   * 
   * @example
   * ```typescript
   * // Set country ID from regional permissions
   * this.userService.setCountryId('US');
   * 
   * // Set multiple country IDs (comma-separated)
   * this.userService.setCountryId('US,CA,MX');
   * 
   * // Clear country restrictions
   * this.userService.setCountryId(null);
   * 
   * // Set from API response
   * this.apiService.getCountryIds(['US', 'EU']).subscribe(response => {
   *   this.userService.setCountryId(response.countryIds);
   * });
   * ```
   */
  setCountryId(countryId: string | null) {
    this.countryIdValue = countryId;
  }

  /**
   * Gets the current country ID for regional access control
   * 
   * @description
   * Returns the current country identifier used for filtering data based on
   * geographic permissions. Components use this value to determine which
   * regional data to display or access.
   * 
   * @returns {string | null} Current country ID or null if no regional restrictions
   * 
   * @example
   * ```typescript
   * // Check if user has regional restrictions
   * const countryId = this.userService.countryId;
   * if (countryId) {
   *   console.log('User has regional access to:', countryId);
   *   this.filterDataByCountry(countryId);
   * } else {
   *   console.log('User has global access');
   *   this.showAllData();
   * }
   * 
   * // Use in API requests
   * const params = {
   *   limit: 100,
   *   ...(this.userService.countryId && { countryId: this.userService.countryId })
   * };
   * this.apiService.getData(params).subscribe(data => console.log(data));
   * 
   * // Parse multiple country IDs
   * const countryIds = this.userService.countryId?.split(',') || [];
   * countryIds.forEach(id => this.processCountryData(id));
   * ```
   */
  get countryId(): string | null {
    return this.countryIdValue;
  }

  get outSourcingGroups(): string[] {
    const user = this.userSubject$.getValue();
    return user?.outsourcingGroups || [];
  }

  /**
   * Clears all user session data including authentication, user profile, privileges, and regional settings
   * 
   * @description
   * Performs a complete session cleanup by resetting all user-related state to initial values.
   * This method should be called during logout, session expiration, or when switching users.
   * All subscribers will receive null values for user and auth observables.
   * 
   * **What gets cleared:**
   * - User profile data (sets user$ to emit null)
   * - Authentication data (sets auth$ to emit null) 
   * - All privilege mappings (resets to empty object)
   * - Country ID regional restrictions (sets to null)
   * 
   * **Effects:**
   * - All components subscribed to user$ will receive null
   * - All components subscribed to auth$ will receive null
   * - Permission checks will return false (empty privilege object)
   * - Regional filtering will be disabled (null country ID)
   * 
   * @example
   * ```typescript
   * // Clear session on logout
   * logout() {
   *   this.userService.clear();
   *   this.router.navigate(['/login']);
   * }
   * 
   * // Clear session on error
   * handleAuthError(error: any) {
   *   console.error('Authentication error:', error);
   *   this.userService.clear();
   *   this.showErrorMessage('Session expired. Please log in again.');
   * }
   * 
   * // Clear session when switching users
   * switchUser(newUserId: string) {
   *   this.userService.clear();
   *   this.authenticateUser(newUserId);
   * }
   * 
   * // Listen for clear events in components
   * ngOnInit() {
   *   this.userService.user$.subscribe(user => {
   *     if (user === null) {
   *       console.log('Session cleared - redirecting to login');
   *       this.handleSessionClear();
   *     }
   *   });
   * }
   * ```
   */
  clear() {
    this.userSubject$.next(null);
    this.authSubject$.next(null);
    this.appUserPrivilege = {};
    this.countryIdValue = null;
  }
}
